import { contractFor, kindName, platformById } from "@ondit/publishing";
import {
	Button,
	Checkbox,
	ChipConfirmModal,
	ChipModal,
	ChipModalBody,
	ChipModalFooter,
	ChipModalHeader,
	ChipSelect,
	Label,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Textarea,
} from "@ondit/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { fileUrl } from "~/common/media/api";
import { PlatformPreview } from "~/common/publishing/platform-preview";
import { useAccess, useExitAccess } from "~/core/access";
import { ApiError } from "~/core/api";
import { useI18n, useT } from "~/core/i18n";
import { article as articlePath } from "~/core/paths";
import { Section } from "~/core/ui/page";
import { holds, marketingKey, type Overview, overview, type Work, workCommand, workState } from "./api";
import "./marketing.css";

/** The posts automation produced, where Distribution keeps its history; review happens here. */
export function DistributionWork({ brandId }: { brandId: string }) {
	const t = useT();
	const data = useOverview(brandId);
	if (!data.data?.work.length) return null;
	return (
		<Section title={t("marketing.posts")} lede={t("marketing.postsHelp")}>
			<WorkList brandId={brandId} data={data.data} />
			<Decisions data={data.data} />
		</Section>
	);
}

function useOverview(brandId: string) {
	return useQuery({
		queryKey: marketingKey(brandId),
		queryFn: () => overview(brandId),
		refetchInterval: 10000,
	});
}
function Decisions({ data }: { data: Overview }) {
	const t = useT();
	const { locale } = useI18n();
	if (!data.decisions.length) return null;
	return (
		<details>
			<summary>{t("marketing.decisions")}</summary>
			{data.decisions.map((d) => (
				<p key={d.id}>
					{new Date(d.created_at).toLocaleString(locale)} · {d.title} · {t(`marketing.reason.${d.reason}`)}
				</p>
			))}
		</details>
	);
}
function WorkList({ brandId, data }: { brandId: string; data: Overview }) {
	const t = useT();
	const { locale } = useI18n();
	// The to-do list opens a work's review directly.
	const [params] = useSearchParams();
	const [selected, setSelected] = useState<string | null>(params.get("work"));
	const selectedWork = data.work.find((work) => work.id === selected);
	if (!data.work.length) return null;
	return (
		<>
			<div className="table-scroll">
				<Table variant="resource">
					<TableHeader>
						<TableRow>
							<TableHead>{t("marketing.content")}</TableHead>
							<TableHead>{t("marketing.state")}</TableHead>
							<TableHead>{t("marketing.cost")}</TableHead>
							<TableHead>{t("marketing.next")}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{data.work.map((work) => (
							<TableRow key={work.id}>
								<TableCell>
									<Link to={articlePath(brandId, work.page_id)}>{work.title}</Link>
									<div className="hint">
										{new Date(work.created_at).toLocaleString(locale)} ·{" "}
										{work.config.channels
											.map((c) => {
												const p = platformById(c.platform);
												return p ? kindName(p, c.format, locale) : c.platform;
											})
											.join(", ")}
									</div>
								</TableCell>
								<TableCell>
									{t(`marketing.state.${workState(work)}`)}
									{work.reason && (
										<div className="hint">
											{work.reason.startsWith("errors.") ? t(work.reason) : t(`marketing.reason.${work.reason}`)}
										</div>
									)}
								</TableCell>
								<TableCell>
									{work.spent}
									{work.held > 0 && <div className="hint">{t("marketing.held", { n: work.held })}</div>}
								</TableCell>
								<TableCell>
									<div className="record-actions">
										{work.variants.length > 0 && (
											<Button variant="default" size="sm" onClick={() => setSelected(work.id)}>
												{t(work.status === "ready" ? "marketing.viewDelivery" : "marketing.review")}
											</Button>
										)}
										{!work.variants.length && work.status !== "blocked" && (
											<Link to={`/w/${brandId}/tasks/agent`}>{t("marketing.runDetails")}</Link>
										)}
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			{selectedWork && <Review brandId={brandId} work={selectedWork} onClose={() => setSelected(null)} />}
		</>
	);
}
function Review({ brandId, work, onClose }: { brandId: string; work: Work; onClose: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	const access = useAccess();
	const exit = useExitAccess();
	const cache = useQueryClient();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [stopping, setStopping] = useState(false);
	const [confirmed, setConfirmed] = useState<string | null>(null);
	const [outcome, setOutcome] = useState(work.outcome);
	const [evidence, setEvidence] = useState(work.evidence);
	async function command(body: { action: string; [key: string]: unknown }, close = false) {
		if (busy) return;
		setBusy(true);
		setError("");
		try {
			await workCommand(brandId, work.id, body);
			toast.success(t(body.action === "approve" ? "marketing.approved" : "common.saved"));
			await cache.invalidateQueries({ queryKey: ["marketing", brandId] });
			if (close) onClose();
			else if (body.action !== "observe") setConfirmed(null);
		} catch (e) {
			const message = e instanceof ApiError ? e.localized(locale) : String(e);
			setError(message);
			toast.error(message);
		} finally {
			setBusy(false);
		}
	}
	// The posts are the page revision's platform versions, which the overview
	// this row came from already carries and refreshes.
	const content = { revision: work.current_revision, variants: work.variants };
	const snapshot = JSON.stringify([content.revision, content.variants.map((v) => [v.id, v.revision])]);
	const checked = snapshot !== null && confirmed === snapshot;
	const ready = work.config.channels.every((c) => content.variants.some((v) => holds(c, v)));
	const needsApproval =
		work.status !== "ready" || work.config.channels.some((c) => !content.variants.find((v) => holds(c, v))?.task_id);
	const missingVariants = !ready;
	return (
		<>
			<ChipModal
				open
				onOpenChange={(open) => {
					if (!open) onClose();
				}}
				srTitle={t("marketing.review")}
				size="xl"
				dismissDisabled={busy}
			>
				<ChipModalHeader onClose={onClose}>{work.title}</ChipModalHeader>
				<ChipModalBody>
					{!work.allowed && <p className="hint">{t("marketing.state.cancelled")}</p>}
					{missingVariants && <p className="error">{t("marketing.needsAdapt")}</p>}

					<div className="marketing-actions">
						<Link to={articlePath(brandId, work.page_id)} onClick={onClose}>
							{t("marketing.edit")}
						</Link>
						<Button
							variant="ghost"
							disabled={!access.editor || busy || !work.allowed || !missingVariants}
							onClick={() => void command({ action: "adapt", revision: content.revision }, false)}
						>
							{t("marketing.refresh")}
						</Button>
						<Button
							variant="ghost"
							disabled={!exit.editor || busy || work.status === "cancelled"}
							onClick={() => setStopping(true)}
						>
							{t("marketing.cancelWork")}
						</Button>
					</div>
					{work.config.channels.map((channel) => {
						const variant = content.variants.find((v) => holds(channel, v));
						const platform = contractFor(channel);
						return variant && platform ? (
							<div key={variant.id} className="marketing-review">
								<PlatformPreview
									platform={platform}
									payload={{
										version: 1,
										platform: platform.id,
										...(platform.format ? { format: platform.format } : {}),
										fields: variant.document.fields,
										media: variant.document.media.map((m) => ({ url: fileUrl(m.file_id), alt: m.alt })),
										source: { page_id: work.page_id, revision: content.revision, canonical: "" },
										target_url: channel.target_url,
									}}
									resolvedFiles={Object.fromEntries(variant.document.media.map((m) => [m.file_id, fileUrl(m.file_id)]))}
									resolvedMedia={variant.document.media
										.filter((m) => m.role === "image" || m.role === "video")
										.map((m) => ({ url: fileUrl(m.file_id), alt: m.alt, kind: m.role as "image" | "video" }))}
								/>
								<p className="hint">
									{variant.task_id
										? t(`marketing.state.${work.variants.find((v) => v.id === variant.id)?.state ?? "ready"}`)
										: channel.credential_id
											? t("marketing.apiAfterApproval")
											: t("marketing.browserAfterApproval")}
								</p>
								{variant.task_id && (
									<Link to={`/w/${brandId}/publish/distribution?tab=prepare&task=${variant.task_id}`} onClick={onClose}>
										{t("marketing.openTask")}
									</Link>
								)}
								{work.variants.find((v) => v.id === variant.id)?.result_url && (
									<a
										href={work.variants.find((v) => v.id === variant.id)!.result_url!}
										target="_blank"
										rel="noreferrer"
									>
										{t("marketing.openPublished")}
									</a>
								)}
							</div>
						) : (
							<p key={channel.platform}>
								{platform?.name}: {t("marketing.needsAdapt")}
							</p>
						);
					})}

					<details>
						<summary>{t("marketing.observe")}</summary>
						<fieldset disabled={!access.editor || busy} className="bare-fieldset marketing-policy">
							<ChipSelect
								fullWidth
								dropdownWidth="trigger"
								aria-label={t("marketing.observe")}
								value={outcome}
								onChange={setOutcome}
								options={["unmeasured", "insufficient", "improved", "unchanged", "worse"].map((value) => ({
									value,
									label: t(`marketing.outcome.${value}`),
								}))}
							/>
							<Label htmlFor={`marketing-${work.id}-evidence`}>{t("marketing.evidence")}</Label>
							<Textarea
								id={`marketing-${work.id}-evidence`}
								value={evidence}
								onChange={(e) => setEvidence(e.target.value)}
								maxLength={4000}
							/>
							<p className="hint">{t("marketing.evidenceHelp")}</p>
							<Button
								variant="default"
								disabled={outcome !== "unmeasured" && !evidence.trim()}
								onClick={() => void command({ action: "observe", outcome, evidence }, false)}
							>
								{t("common.save")}
							</Button>
						</fieldset>
					</details>

					{error && (
						<p role="alert" className="error">
							{error}
						</p>
					)}
				</ChipModalBody>
				<ChipModalFooter
					defaultAction="dismiss"
					onCancel={onClose}
					cancelLabel={t("common.close")}
					leadingContent={
						needsApproval ? (
							<label htmlFor={`marketing-${brandId}-12`} className="marketing-check">
								<Checkbox
									id={`marketing-${brandId}-12`}
									checked={checked}
									onCheckedChange={(v) => setConfirmed(v === true ? snapshot : null)}
									disabled={!access.editor || busy || !work.allowed || !ready}
								/>
								{t("marketing.confirmRevision", { n: content.revision })}
							</label>
						) : undefined
					}
					primaryAction={
						needsApproval
							? {
									label: t(busy ? "marketing.working" : "marketing.approve"),
									disabled: !checked || !ready || !access.editor || busy || !work.allowed,
									onClick: () => {
										void command({
											action: "approve",
											revision: content.revision,
											variants: content.variants.map((v) => ({ id: v.id, revision: v.revision })),
										});
									},
								}
							: undefined
					}
				/>
			</ChipModal>
			<ChipConfirmModal
				open={stopping}
				onOpenChange={setStopping}
				title={t("marketing.cancelWork")}
				text={t("marketing.stopHelp")}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("marketing.cancelWork"),
					pending: busy,
					onClick: () => void command({ action: "cancel" }, true),
				}}
			/>
		</>
	);
}
